import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from './context';
import { ProviderLogo, type AuthProviderId } from './ProviderLogos';
import { ACCOUNT_BASE_PATH } from './pages/accountPaths';

const providerLabel = (provider: AuthProviderId): string => {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    default:
      return provider;
  }
};

export const UserCard = () => {
  const { user } = useUser();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      const res = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Logout failed (${res.status})`);
      }
      window.location.assign('/');
      window.location.reload();
    } catch (e) {
      setLoggingOut(false);
      console.error(e);
    }
  }, []);

  if (!user) return null;

  const first = user.firstName?.trim() || '—';
  const last = user.lastName?.trim() || '—';

  return (
    <aside
      className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="account-heading"
    >
      <h2 id="account-heading" className="font-display text-xl font-semibold text-ink">
        Your account
      </h2>
      <p className="mt-1 font-sans text-sm leading-relaxed text-ink/65">
        Welcome—you’re all set to use OnTime for smooth, friendly school check-ins.
      </p>

      <Link
        to={ACCOUNT_BASE_PATH}
        className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-skysoft/60 px-4 py-3 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/35"
      >
        Open your account
      </Link>

      <dl className="mt-6 space-y-4 font-sans text-sm">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            First name
          </dt>
          <dd className="mt-1 font-semibold text-ink">{first}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            Last name
          </dt>
          <dd className="mt-1 font-semibold text-ink">{last}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            Sign-in provider
          </dt>
          <dd className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-ink/10 bg-white shadow-sm"
                title={providerLabel(user.provider)}
                aria-label={providerLabel(user.provider)}
              >
                <ProviderLogo provider={user.provider} className="h-6 w-6" />
              </span>
              <span className="font-semibold text-ink">{providerLabel(user.provider)}</span>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-full px-3 py-1.5 font-sans text-sm font-semibold text-ink/70 underline decoration-ink/25 underline-offset-2 transition hover:bg-ink/5 hover:text-ink hover:decoration-ink/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:opacity-50"
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              aria-busy={loggingOut}
            >
              {loggingOut ? 'Signing out…' : 'Log out'}
            </button>
          </dd>
        </div>
      </dl>
    </aside>
  );
};
